import { useMemo, useEffect, useRef } from 'react'
import { Avatar } from '@mui/material'
import SmartToy from '@mui/icons-material/SmartToy'
import defaultAvatar from '../../../icon/defaultAvatars/user.png'
import '../../workspace/css/workspace.scss'

const WELCOME_MESSAGE = {
  id: 'welcome-msg',
  sender: 'ai',
  content: "Hey I am your AI assistant, feel free to ask me questions if you are stuck!",
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// welcomeMessage: null for a chat that opened without one; undefined keeps the default.
// highlightIds: { from, to } message ids just added (replay change view), shown in green.
// Applied to the bubble itself (the <pre>, which carries the bubble's background and shape).
const NEW_MESSAGE = { boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.7)', backgroundColor: '#e9f9ef' }

export default function ReplayChatWindow({ messages = [], currentInput = '', isAiProcessing = false, startTime = 0, studentName = 'Student', welcomeMessage, highlightIds = null }) {  const chatHistoryRef = useRef(null)

  const allMessages = useMemo(() => {
    if (welcomeMessage === null) return messages
    const welcome = { ...WELCOME_MESSAGE, ...(welcomeMessage ? { content: welcomeMessage } : {}), timestamp: startTime || null }
    if (messages.length > 0 && messages[0].id === 'welcome-msg') return messages
    return [welcome, ...messages]
  }, [messages, startTime, welcomeMessage])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
    }
  }, [allMessages, isAiProcessing, currentInput])

  return (
    <div 
      className="ws-chat-container" 
      style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden' 
      }}
    >
      <div 
        className="ws-chat" 
        style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0, 
          overflow: 'hidden' 
        }}
      >
        <div 
          className="ws-chat-history" 
          ref={chatHistoryRef} 
          style={{ 
            flex: '1 1 0', 
            minHeight: 0, 
            overflowY: 'auto' 
          }}
        >
          {allMessages.map((msg) => (
            <div key={msg.id} className={`ws-message ${msg.sender === 'user' ? 'ws-owner' : ''}`}>
              <div className="ws-message-info">
                {msg.sender === 'ai' ? (
                  <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                    <SmartToy sx={{ fontSize: '1rem' }} />
                  </Avatar>
                ) : (
                  <img src={defaultAvatar} alt="user" />
                )}
                <span className="ws-info-name">{msg.sender === 'ai' ? 'AI Assistant' : studentName}</span>
                <span className="ws-info-time">{formatTime(msg.timestamp)}</span>
              </div>
              <div className="ws-message-content-wrapper">
                <div className="ws-message-content">
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', ...(highlightIds && msg.id >= highlightIds.from && msg.id <= highlightIds.to ? NEW_MESSAGE : null) }}>{msg.content}</pre>
                </div>
              </div>
            </div>
          ))}

          {/* AI Thinking Indicator */}
          {isAiProcessing && (
            <div className="ws-message">
              <div className="ws-message-info">
                <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                  <SmartToy sx={{ fontSize: '1rem' }} />
                </Avatar>
                <span className="ws-info-name">AI Assistant</span>
              </div>
              <div className="ws-message-content-wrapper">
                <div className="ws-message-content" style={{ 
                  color: '#888', 
                  fontStyle: 'italic',
                  marginTop: '28px' 
                }}>
                  Thinking...
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ws-message-form" style={{ flexShrink: 0 }}>
          <textarea
            className="ws-message-input"
            rows={1}
            value={currentInput}
            readOnly
            style={{ cursor: 'default' }}
            placeholder={allMessages.length <= (welcomeMessage === null ? 0 : 1) ? "No messages yet..." : ""}
          />
          <div className="ws-char-count">
            <span style={{ color: '#999' }}>
              {currentInput.length > 0 ? `${currentInput.length}/500` : ''}
            </span>
          </div>
          <button className="ws-send-button" disabled style={{ opacity: 0.5 }}>
            Send
          </button>
        </div>
      </div>
    </div>
  )
}